import { FormEvent, useContext, useEffect, useState } from 'react';
import RadioOptions from '../../common/RadioOptions/RadioOptions';
import TextInput from '../../common/TextInput/TextInput';
import { ButtonCommand } from '../../common/Button/ButtonCommand';
import {
  getDynamicHelpText,
  populateDatalist,
  populateSelectOptions,
  renderRadioOptions,
  setCountryCode,
  renderRadioSelectOptions
} from '../../common/HelperMethods';
import { SiteContext } from '../../../context/SiteContext';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import SelectInput, {
  SelectInputHelper
} from '../../common/SelectInput/SelectInput';
import {
  CarInsuranceVehicleModel,
  DatalistOption,
  DropdownData,
  InsuranceJourneyContext,
  KeyValuePair,
  Modification,
  SelectOpt,
  VehicleModificationError,
  VehicleType
} from '../../common/CommonTypes';
import { t } from 'i18next';
import RadioSelection from '../../common/Product/RadioSelection';
import { VehicleApi } from '../../../api/VehicleApi';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import SearchTextInput from '../../common/SerachTextInput/SearchTextInput';
import {
  InsuranceJourneyHelperText,
  PageAbsolutePath,
  DefaultRadioOptions,
  InsuranceSaveForLaterMessage
} from '../../../consts/Common';
import ClickableHelpText from '../../common/HelpText/ClickableHelpText';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import VehicleModifications from '../../common/VehicleModifications/VehicleModifications';
import SimpleDateInput from '../../common/DateInput/SimpleDateInput';
import { PagesWithDropdown, Source, Vendor } from '../../common/Constants';
import useCarInsuranceController from '../../../hooks/insuranceJourney/useCarInsuranceController';
import LoadingContainer from '../../common/LoadingContainer/LoadingContainer';
import { useErrorBoundary } from 'react-error-boundary';

export type PageErrors = {
  vrnKnown: string;
  userVrn: string;
  manufacturer: string;
  model: string;
  fuelType: string;
  transmission: string;
  numberOfDoors: string;
  isItNewOrUsed: string;
  vehicleSpecification: string;
  vendor: string;
  yearOfManufacture: string;
  typeOfTrackingDevice: string;
  modifications: VehicleModificationError[];
  pickupDealer: string;
  keysPickupDate: string;
  isFittedWithAntiTheftDevice: string;
  isVehicleModified: string;
  carValue: string;
};

export const VrnSearchStatus = {
  NoSearch: 0,
  VrnFound: 1,
  VrnNotFound: 2,
  ManualFound: 3,
  ManualNotFound: 4
} as const;

export const VehicleDetailsPage = (): JSX.Element => {
  const initialErrors: PageErrors = {
    vrnKnown: '',
    userVrn: '',
    manufacturer: '',
    model: '',
    fuelType: '',
    transmission: '',
    numberOfDoors: '',
    isItNewOrUsed: '',
    vehicleSpecification: '',
    vendor: '',
    yearOfManufacture: '',
    typeOfTrackingDevice: '',
    modifications: [],
    pickupDealer: '',
    keysPickupDate: '',
    isFittedWithAntiTheftDevice: '',
    isVehicleModified: '',
    carValue: ''
  };

  const { journey, pageEvents } = useOutletContext<InsuranceJourneyContext>();
  const { vehicleInfo, staticData, source, savingQuote, pageLoading } = journey;
  const { vehicleDetailsPage, commonEvents } = pageEvents;

  const {
    vrnKnown,
    userVrn,
    fuelType,
    manufacturer,
    model,
    numberOfDoors,
    transmissionType,
    isItNewOrUsed,
    yearOfManufacture,
    keysPickupDate,
    vehicle,
    hasBeenModified,
    hasTrackingDevice,
    trackingDevice,
    trackingDeviceKey,
    modifications,
    vendor,
    dealer,
    carValue,
    vehicleSpecification
  } = vehicleInfo;

  const {
    onChangeVrnKnown,
    onChangeUserVrn,
    onChangeManufacturer,
    onChangeModel,
    onChangeFuelType,
    onChangeTransmissionType,
    onChangeNumberOfDoors,
    onChangeIsItNewOrUsed,
    onChangeYearOfManufacture,
    onChangeKeysPickupDate,
    onChangeVehicle,
    onChangeHasTrackingDevice,
    onChangeTrackingDevice,
    onChangeVehicleHasBeenModified,
    onChangeVendor,
    onChangeDealer,
    onChangeCarvalue,
    onUpdateModificationList,
    onChangeVehicleSpecification
  } = vehicleDetailsPage;

  const { setSavingQuote, onPageChange, setStaticData, setPageLoading } =
    commonEvents;
  const navigate = useNavigate();
  const { showBoundary } = useErrorBoundary();
  const [showNonSchemePopup, setShowNonSchemePopup] = useState(false);
  const [showManualSearchForm, setShowManualSearchForm] = useState(false);

  const {
    getVehicleDetailsPageDropdownValues,
    getDropDownValuesForVehicleSpecifications,
    getDropdownValuesForVehicleMake,
    getDropdownValuesForVehicleModel,
    getTrackingDevicesBySearch
  } = useCarInsuranceController();

  const onBackendFailure = (err: string) => {
    showBoundary(new Error(err));
  };

  const trackingDeviceErrorMessage =
    'Please re-type, wait for options to appear and select from the list.';
  const [errors, setErrors] = useState<PageErrors>(initialErrors);
  const yesNoSelectOption: SelectOpt[] = [
    { id: 'Yes', disabled: false, text: 'Yes', value: 'Yes' },
    { id: 'No', disabled: false, text: 'No', value: 'No' }
  ];

  const transmissionList = ['Manual', 'Automatic'];
  const [vehicleSearchResult, setVehicleSearchResult] = useState(0);
  const [showSearchMessage, setShowSearchMessage] = useState(false);
  const [trackingDeviceTypes, setTrackingDeviceTypes] = useState<
    DatalistOption[]
  >([]);

  const { site } = useContext(SiteContext);
  const token = site.user.token;

  const { brand } = site;

  const today = new Date();
  const minPurchaseDate = new Date(today.setFullYear(today.getFullYear() - 10));
  const maxPurchaseDate = new Date();
  maxPurchaseDate.setDate(maxPurchaseDate.getDate() + 60);

  //derived state
  const dealers =
    staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.InsuranceVehicleDetailsPage &&
        d.controlName === 'dealers'
    )?.values ?? [];
  const fuelTypes =
    staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.InsuranceVehicleDetailsPage &&
        d.controlName === 'fuel-types'
    )?.values ?? [];
  const modificationTypes =
    staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.InsuranceVehicleDetailsPage &&
        d.controlName === 'modification-types'
    )?.values ?? [];
  const numberOfDoorsValues =
    staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.InsuranceVehicleDetailsPage &&
        d.controlName === 'no-of-doors'
    )?.values ?? [];
  const vendors =
    staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.InsuranceVehicleDetailsPage &&
        d.controlName === 'vendors'
    )?.values ?? [];
  const yearOfManufactureValues =
    staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.InsuranceVehicleDetailsPage &&
        d.controlName === 'year-of-manufacture'
    )?.values ?? [];
  const manufacturersOptions =
    staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.InsuranceVehicleDetailsPage &&
        d.controlName === 'vehicle-makes'
    )?.values ?? [];
  const modelsOptions =
    staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.InsuranceVehicleDetailsPage &&
        d.controlName === 'models'
    )?.values ?? [];
  const vehicleSpecificationsOptions =
    staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.InsuranceVehicleDetailsPage &&
        d.controlName === 'vehicle-specifications'
    )?.values ?? [];
  const newOrUsedOptions =
    staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.InsuranceVehicleDetailsPage &&
        d.controlName === 'new-used'
    )?.values ?? [];

  useEffect(() => {
    window.scrollTo(0, 0);
    let initialStaticData = staticData ? [...staticData] : [];

    const getSpecificationsSuccess = (
      vehicleSpecifications: KeyValuePair[]
    ) => {
      const newStaticDataItem = {
        page: PagesWithDropdown.InsuranceVehicleDetailsPage,
        controlName: 'vehicle-specifications',
        values: populateSelectOptions(vehicleSpecifications)
      };

      setStaticData([...initialStaticData, newStaticDataItem]);
    };

    const getModelsSuccess = (vehicleModels: KeyValuePair[]) => {
      const newStaticDataItem = {
        page: PagesWithDropdown.InsuranceVehicleDetailsPage,
        controlName: 'models',
        values: populateSelectOptions(vehicleModels)
      };

      initialStaticData = [...initialStaticData, newStaticDataItem];

      if (
        vehicleSpecification &&
        vehicleSpecification !== SelectInputHelper.NullValue()
      ) {
        getDropDownValuesForVehicleSpecifications(
          manufacturer,
          model,
          fuelType,
          transmissionType,
          numberOfDoors,
          token,
          getSpecificationsSuccess,
          getDropDownValuesForVehicleSpecificationsFailure
        );
      } else {
        setStaticData(initialStaticData);
      }
    };

    const getMakesSuccess = (vehicleMakes: KeyValuePair[]) => {
      const newStaticDataItem = {
        page: PagesWithDropdown.InsuranceVehicleDetailsPage,
        controlName: 'vehicle-makes',
        values: populateSelectOptions(vehicleMakes)
      };

      initialStaticData = [...initialStaticData, newStaticDataItem];

      if (manufacturer && manufacturer !== SelectInputHelper.NullValue()) {
        getDropdownValuesForVehicleModel(
          manufacturer,
          token,
          getModelsSuccess,
          onBackendFailure
        );
      } else {
        setStaticData(initialStaticData);
      }
    };

    const getPageDataSuccess = (dropdownValues: CarInsuranceVehicleModel) => {
      const newValues = [
        {
          page: PagesWithDropdown.InsuranceVehicleDetailsPage,
          controlName: 'dealers',
          values: populateSelectOptions(dropdownValues.dealers)
        },
        {
          page: PagesWithDropdown.InsuranceVehicleDetailsPage,
          controlName: 'fuel-types',
          values: populateSelectOptions(dropdownValues.fuelTypes)
        },
        {
          page: PagesWithDropdown.InsuranceVehicleDetailsPage,
          controlName: 'modification-types',
          values: populateSelectOptions(dropdownValues.modificationTypes)
        },
        {
          page: PagesWithDropdown.InsuranceVehicleDetailsPage,
          controlName: 'no-of-doors',
          values: populateSelectOptions(dropdownValues.numberOfDoors)
        },
        {
          page: PagesWithDropdown.InsuranceVehicleDetailsPage,
          controlName: 'vendors',
          values: populateSelectOptions(dropdownValues.vendors)
        },
        {
          page: PagesWithDropdown.InsuranceVehicleDetailsPage,
          controlName: 'year-of-manufacture',
          values: populateSelectOptions(dropdownValues.yearOfManufacture)
        },
        {
          page: PagesWithDropdown.InsuranceVehicleDetailsPage,
          controlName: 'new-used',
          values: populateSelectOptions(dropdownValues.newOrUsed)
        }
      ];

      initialStaticData = [...initialStaticData, ...newValues];

      getDropdownValuesForVehicleMake(token, getMakesSuccess, onBackendFailure);
    };

    const pageData = staticData?.find(
      (d) => d.page === PagesWithDropdown.InsuranceVehicleDetailsPage
    );

    if (!pageData) {
      getVehicleDetailsPageDropdownValues(
        site.brand.tiaProductCode,
        token,
        getPageDataSuccess,
        onBackendFailure
      );
    }

    setShowManualSearchForm(!vrnKnown || (vrnKnown && model) ? true : false);
  }, []);

  useEffect(() => {
    showNoneSchemePopup(vehicle?.make ?? '');
  }, [vehicle?.make ?? '']);

  const showNoneSchemePopup = (make: string) => {
    if (
      make &&
      make !== SelectInputHelper.NullValue() &&
      make.toLowerCase() !== site.brand.brandName.toLowerCase() &&
      site.brand.brandName.toLowerCase() !== 'original'
    ) {
      setShowNonSchemePopup(true);
    }
  };

  const onChangingManufacturer = (value: string) => {
    const make = getManufacturerName(value);
    showNoneSchemePopup(make);
    onChangeManufacturer(value);
    setVehicleSearchResult(VrnSearchStatus.NoSearch);

    if (value && value !== SelectInputHelper.NullValue()) {
      getDropdownValuesForVehicleModel(
        value,
        token,
        getDropdownValuesForVehicleModelsSuccess,
        onBackendFailure
      );
    } else {
      setStaticData(removeDropDownData(staticData, 'models'));
    }
  };

  const onChangingModel = (value: string) => {
    onChangeModel(value);
    setVehicleSearchResult(VrnSearchStatus.NoSearch);
    setStaticData(removeDropDownData(staticData, 'vehicle-specifications'));
  };

  const onChangingFuelType = (value: string) => {
    onChangeFuelType(value);
    setVehicleSearchResult(VrnSearchStatus.NoSearch);
    setVehicleSearchResult(VrnSearchStatus.NoSearch);
    setStaticData(removeDropDownData(staticData, 'vehicle-specifications'));
  };

  const onChangingTransmissionType = (value: string) => {
    onChangeTransmissionType(value);
    setVehicleSearchResult(VrnSearchStatus.NoSearch);
    setStaticData(removeDropDownData(staticData, 'vehicle-specifications'));
  };

  const onChangingNumberOfDoors = (value: string) => {
    onChangeNumberOfDoors(value);
    setVehicleSearchResult(VrnSearchStatus.NoSearch);
    setStaticData(removeDropDownData(staticData, 'vehicle-specifications'));
  };

  const validate = (): boolean => {
    let isValid = true;
    const error: PageErrors = {
      ...initialErrors
    };

    if (
      !keysPickupDate ||
      !CommonValidation.isValidDate(keysPickupDate, false)
    ) {
      error.keysPickupDate = 'Date is required';
      isValid = false;
    }

    if (!newOrUsedOptions.some((p) => p.value === isItNewOrUsed)) {
      error.isItNewOrUsed = 'This field is required';
      isValid = false;
    }

    if (yearOfManufacture == SelectInputHelper.NullValue()) {
      error.yearOfManufacture = 'Year is required';
      isValid = false;
    }

    if (hasTrackingDevice === undefined || hasTrackingDevice === null) {
      error.isFittedWithAntiTheftDevice = 'This field is required';
      isValid = false;
    }

    if (hasTrackingDevice && CommonValidation.isEmpty(trackingDeviceKey)) {
      error.typeOfTrackingDevice = 'Device type is required';
      isValid = false;
    }

    if (hasBeenModified === undefined || hasBeenModified === null) {
      error.isVehicleModified = 'This field is required';
      isValid = false;
    }

    if (hasBeenModified) {
      error.modifications = modifications.map((v) => {
        const e: VehicleModificationError = {};
        if (
          SelectInputHelper.isNullValue(v.forDisability) ||
          SelectInputHelper.isEmptyValue(v.forDisability)
        )
          e.forDisability = 'Field is required';

        if (
          SelectInputHelper.isNullValue(v.modificationType) ||
          SelectInputHelper.isEmptyValue(v.modificationType)
        )
          e.modificationType = 'Modification type is required';

        return e;
      });

      if (
        error.modifications.filter(
          (e) =>
            CommonValidation.isNotEmpty(e.forDisability) ||
            CommonValidation.isNotEmpty(e.modificationType)
        ).length > 0
      )
        isValid = false;
    }

    if (
      hasBeenModified &&
      modifications.filter(
        (m) =>
          m.forDisability != SelectInputHelper.NullValue() &&
          m.modificationType != SelectInputHelper.NullValue()
      ).length == 0
    ) {
      error.isVehicleModified = 'At least one modification is required';
      isValid = false;
    }

    if (vrnKnown) {
      if (CommonValidation.isEmpty(userVrn)) {
        error.userVrn = 'Registration number is required';
        isValid = false;
      }

      if (!showManualSearchForm) {
        if (
          CommonValidation.isEmpty(vehicle?.vrn) &&
          vehicleSearchResult === VrnSearchStatus.NoSearch
        ) {
          setShowSearchMessage(true);
          isValid = false;
        }

        if (vehicleInfo === undefined) {
          setShowSearchMessage(true);
          isValid = false;
        }
      }
    }

    if (showManualSearchForm) {
      let hasManualSelectionMissing = false;

      if (
        SelectInputHelper.isNullValue(manufacturer) ||
        SelectInputHelper.isEmptyValue(manufacturer)
      ) {
        error.manufacturer = 'Manufacturer is required';
        isValid = false;
        hasManualSelectionMissing = true;
      }
      if (
        SelectInputHelper.isNullValue(model) ||
        SelectInputHelper.isEmptyValue(model)
      ) {
        error.model = 'Model is required';
        isValid = false;
        hasManualSelectionMissing = true;
      }
      if (
        SelectInputHelper.isNullValue(fuelType) ||
        SelectInputHelper.isEmptyValue(fuelType)
      ) {
        error.fuelType = 'Fuel type is required';
        isValid = false;
        hasManualSelectionMissing = true;
      }
      if (CommonValidation.isEmpty(transmissionType)) {
        error.transmission = 'Transmission type is required';
        isValid = false;
        hasManualSelectionMissing = true;
      }
      if (
        SelectInputHelper.isNullValue(numberOfDoors) ||
        SelectInputHelper.isEmptyValue(numberOfDoors)
      ) {
        error.numberOfDoors = 'Number of doors is required';
        isValid = false;
        hasManualSelectionMissing = true;
      }

      if (
        SelectInputHelper.isNullValue(vehicleSpecification) ||
        SelectInputHelper.isEmptyValue(vehicleSpecification)
      ) {
        if (
          vehicleSearchResult === VrnSearchStatus.ManualFound ||
          vehicleSpecificationsOptions.length > 0
        ) {
          error.vehicleSpecification = 'Vehicle specifications is required';
        } else {
          if (!hasManualSelectionMissing) {
            setShowSearchMessage(true);
          }
        }
        isValid = false;
      }
    }

    if (
      brand.showInsuranceVendorDropdown &&
      (!vendor || vendor === SelectInputHelper.NullValue())
    ) {
      error.vendor = 'Vendor is required';
      isValid = false;
    }

    if (
      vendor === Vendor.FranchiseRetailer &&
      dealer == SelectInputHelper.NullValue()
    ) {
      error.pickupDealer = 'Dealer is required';
      isValid = false;
    }

    if (CommonValidation.isEmpty(carValue)) {
      error.carValue = 'Car value is required';
      isValid = false;
    }

    setErrors(error);

    return isValid;
  };

  const onNextClick = () => {
    if (validate()) {
      const path: string = setCountryCode(
        PageAbsolutePath.Insurance.InsuranceCoverDetailsPage,
        site.countryCode
      );
      navigate(path);
      onPageChange(PageAbsolutePath.Insurance.VehicleDetails, true);
    }
  };

  const onBackClick = () => {
    const path: string = setCountryCode(
      PageAbsolutePath.Insurance.DrivingHistory,
      site.countryCode
    );
    navigate(path);
  };

  const fetchVehicleSuccess = (userVehicle: VehicleType) => {
    if (
      userVehicle.registrationDate === '' ||
      userVehicle.registrationDate === null
    ) {
      setVehicleSearchResult(VrnSearchStatus.VrnNotFound);
      onChangeVehicle(undefined);
      setShowManualSearchForm(true);

      if (manufacturersOptions.length === 0) {
        getDropdownValuesForVehicleMake(
          token,
          getDropdownValuesForVehicleMakeSuccess,
          onBackendFailure
        );
      }
    } else {
      setVehicleSearchResult(VrnSearchStatus.VrnFound);
      onChangeVehicle(userVehicle);
      setShowManualSearchForm(false);
    }
  };

  const fetchVehicleFailure = () => {
    setVehicleSearchResult(VrnSearchStatus.VrnNotFound);
    onChangeVehicle(undefined);
    setShowManualSearchForm(true);

    if (manufacturersOptions.length === 0) {
      getDropdownValuesForVehicleMake(
        token,
        getDropdownValuesForVehicleMakeSuccess,
        onBackendFailure
      );
    }
  };

  const loadVehicleByVrn = async () => {
    const controller = new AbortController();

    await new VehicleApi().getRegisteredVehicles(
      userVrn,
      site.user.token,
      fetchVehicleSuccess,
      fetchVehicleFailure,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const getDropDownValuesForVehicleSpecificationsSuccess = (
    specifications: KeyValuePair[]
  ) => {
    const newStaticDataItem = {
      page: PagesWithDropdown.InsuranceVehicleDetailsPage,
      controlName: 'vehicle-specifications',
      values: populateSelectOptions(specifications)
    };

    const hasVehicleSpecificationsOptions = staticData.find(
      (d) =>
        d.page === PagesWithDropdown.InsuranceVehicleDetailsPage &&
        d.controlName === 'vehicle-specifications'
    );

    if (hasVehicleSpecificationsOptions) {
      const newStaticData = staticData.map((d) => {
        if (
          d.page === PagesWithDropdown.InsuranceVehicleDetailsPage &&
          d.controlName === 'vehicle-specifications'
        ) {
          return newStaticDataItem;
        } else {
          return d;
        }
      });

      setStaticData(newStaticData);
    } else {
      setStaticData([...staticData, newStaticDataItem]);
    }

    if (specifications.length === 0) {
      setVehicleSearchResult(VrnSearchStatus.ManualNotFound);
    } else {
      setVehicleSearchResult(VrnSearchStatus.ManualFound);
      setShowSearchMessage(false);
    }
  };

  const getDropDownValuesForVehicleSpecificationsFailure = (err: string) => {
    console.log(err);
    setVehicleSearchResult(VrnSearchStatus.ManualNotFound);
  };

  const onFindVehicle = (): void => {
    setShowSearchMessage(false);
    loadVehicleByVrn();

    setErrors({
      ...errors,
      userVrn: ''
    });
  };

  const onFindVehicleBySelectOptions = (): void => {
    setShowSearchMessage(false);
    setErrors({
      ...errors,
      manufacturer: '',
      model: '',
      fuelType: '',
      transmission: '',
      numberOfDoors: '',
      vehicleSpecification: ''
    });
    getDropDownValuesForVehicleSpecifications(
      manufacturer,
      model,
      fuelType,
      transmissionType,
      numberOfDoors,
      token,
      getDropDownValuesForVehicleSpecificationsSuccess,
      getDropDownValuesForVehicleSpecificationsFailure
    );
  };

  const onChangingVrnKnown = (vrnKnown: boolean) => {
    const brand = site.brand.brandName.toUpperCase();

    setShowSearchMessage(false);
    setShowManualSearchForm(!vrnKnown);
    setVehicleSearchResult(VrnSearchStatus.NoSearch);

    if (manufacturer !== brand) {
      getDropdownValuesForVehicleModel(
        brand,
        token,
        getDropdownValuesForVehicleModelsSuccess,
        onBackendFailure
      );
    } else {
      const updatedStaticData = removeDropDownData(
        staticData,
        'vehicle-specifications'
      );
      setStaticData(updatedStaticData);
    }

    onChangeVrnKnown(vrnKnown, brand);

    if (!vrnKnown && manufacturersOptions.length === 0) {
      getDropdownValuesForVehicleMake(
        token,
        getDropdownValuesForVehicleMakeSuccess,
        onBackendFailure
      );
    }
  };

  const getManufacturerName = (value: string) => {
    return manufacturersOptions.find((m) => m.value === value)?.text ?? '';
  };

  const removeDropDownData = (
    data: DropdownData[],
    dataToRemove: string
  ): DropdownData[] => {
    return data.filter(
      (d) =>
        !(
          d.page === PagesWithDropdown.InsuranceVehicleDetailsPage &&
          d.controlName === dataToRemove
        )
    );
  };

  const getDropdownValuesForVehicleMakeSuccess = (
    vehicleMakes: KeyValuePair[]
  ) => {
    const newStaticDataItem = {
      page: PagesWithDropdown.InsuranceVehicleDetailsPage,
      controlName: 'vehicle-makes',
      values: populateSelectOptions(vehicleMakes)
    };

    if (staticData) {
      let updatedStaticData: DropdownData[] = removeDropDownData(
        staticData,
        'models'
      );
      updatedStaticData = removeDropDownData(
        updatedStaticData,
        'vehicle-specifications'
      );

      setStaticData([...updatedStaticData, newStaticDataItem]);
    } else {
      setStaticData([newStaticDataItem]);
    }
  };

  const getDropdownValuesForVehicleModelsSuccess = (
    vehicleModels: KeyValuePair[]
  ) => {
    const newStaticDataItem = {
      page: PagesWithDropdown.InsuranceVehicleDetailsPage,
      controlName: 'models',
      values: populateSelectOptions(vehicleModels)
    };

    if (!staticData) {
      setStaticData([newStaticDataItem]);
    } else {
      const hasModelsOptions = staticData.find(
        (d) =>
          d.page === PagesWithDropdown.InsuranceVehicleDetailsPage &&
          d.controlName === 'models'
      );

      const updatedStaticData: DropdownData[] = removeDropDownData(
        staticData,
        'vehicle-specifications'
      );

      if (hasModelsOptions) {
        const newStaticData = updatedStaticData.map((d) => {
          if (
            d.page === PagesWithDropdown.InsuranceVehicleDetailsPage &&
            d.controlName === 'models'
          ) {
            return newStaticDataItem;
          } else {
            return d;
          }
        });

        setStaticData(newStaticData);
      } else {
        setStaticData([...updatedStaticData, newStaticDataItem]);
      }
    }
  };

  const handleOnChangeKeysPickupDate = (date: Date | null) => {
    onChangeKeysPickupDate(date === null ? undefined : date);
  };

  const handleOnLeaveTypeOfTrackingDevice = (
    e: React.FormEvent<HTMLInputElement>
  ) => {
    getTrackingDevicesBySearch(
      e.currentTarget.value,
      token,
      validateTrackingDeviceSuccess,
      onBackendFailure
    );
  };

  const validateTrackingDeviceSuccess = (trackingDevices: KeyValuePair[]) => {
    const isValidTrackingDevice =
      trackingDevices.filter((o) => o.value === trackingDevice).length > 0;

    if (isValidTrackingDevice) {
      setErrors({ ...errors, typeOfTrackingDevice: '' });
    } else {
      setErrors({
        ...errors,
        typeOfTrackingDevice: trackingDeviceErrorMessage
      });
      onChangeTrackingDevice('', '');
      setTrackingDeviceTypes(populateDatalist([]));
    }
  };

  const handleTypeOfTrackingDeviceChange = (
    event: FormEvent<HTMLInputElement>
  ): void => {
    errors.typeOfTrackingDevice &&
      setErrors({ ...errors, typeOfTrackingDevice: '' });
    const newTrackingDeviceKey = trackingDeviceTypes.find(
      (td) => td.text === event.currentTarget.value
    )?.value;
    onChangeTrackingDevice(event.currentTarget.value, newTrackingDeviceKey);
    loadTypeOfTrackingDevicesBySearch(event.currentTarget.value);
  };

  const handleCarValueChange = (event: FormEvent<HTMLInputElement>): void => {
    const numericValue = event.currentTarget.value.replace(/[^0-9]/g, '');
    onChangeCarvalue(numericValue);
  };

  const loadTypeOfTrackingDevicesBySearch = (value: string) => {
    if (value.length > 2)
      getTrackingDevicesBySearch(
        value,
        token,
        setTrackingDevicesListSuccess,
        onBackendFailure
      );
    else setTrackingDeviceTypes(populateDatalist([]));
  };

  const setTrackingDevicesListSuccess = (trackingDevices: KeyValuePair[]) => {
    setTrackingDeviceTypes(populateDatalist(trackingDevices));
  };

  const onSaveLaterClick = (): void => {
    setPageLoading(true);
    setSavingQuote(true);
  };

  return (
    <LoadingContainer
      loading={pageLoading}
      message={InsuranceSaveForLaterMessage}>
      <>
        <div className='one-col'>
          <ButtonCommand
            name='btn-back-top'
            text={'< Back'}
            onClick={onBackClick}
          />
        </div>
        <h4 className='section-header m1-top'>Vehicle Details</h4>
        <div className='table-flex-layout'>
          <div className='form-container'>
            <div className='one-col'>
              <RadioOptions
                errorMessage={errors.vrnKnown}
                selectedOption={
                  vrnKnown === null ? '' : vrnKnown ? 'Yes' : 'No'
                }
                optionsList={DefaultRadioOptions}
                setSelectedOption={(value) =>
                  onChangingVrnKnown(value === 'Yes')
                }
                label={'Do you know your vehicle registration number?'}
                name='vrn-known'
              />
            </div>
            {vrnKnown && (
              <div className='one-col'>
                <div className='control-label'>Registration number</div>
                <div className='flex-row'>
                  <div className='control flex-column'>
                    <TextInput
                      placeholder='Registration number'
                      name='registration-number'
                      className='input-text w250'
                      value={userVrn}
                      onChange={(e: React.FormEvent<HTMLInputElement>) =>
                        onChangeUserVrn(e.currentTarget.value)
                      }
                      error={errors.userVrn}
                      readOnly={false}
                    />
                  </div>
                </div>
                {
                  <div className='one-col m1-top'>
                    <ButtonContinue
                      name='find-vehicle'
                      text='Find vehicle'
                      onClick={onFindVehicle}
                      additionalClassNames='cms-btn-find btn-find'
                    />
                  </div>
                }
              </div>
            )}
            {vehicleSearchResult === VrnSearchStatus.VrnNotFound && (
              <div className='one-col error-style'>
                Vehicle details not found, please select model from the list
                below and click Find vehicle
              </div>
            )}
            {showManualSearchForm && (
              <>
                <div className='two-col'>
                  <div className='control-label help-text'>
                    Manufacturer
                    <ClickableHelpText
                      helpText={getDynamicHelpText(
                        InsuranceJourneyHelperText.Pages.VehicleDetails
                          .Manufacturer,
                        '[Brand]',
                        site.brand.brandName
                      )}
                    />
                  </div>
                  <div className='control'>
                    <SelectInput
                      name='manufacturer'
                      className='input-text w250'
                      value={manufacturer}
                      options={manufacturersOptions}
                      onChange={onChangingManufacturer}
                      addDefaultOption
                      error={errors.manufacturer}
                    />
                  </div>
                </div>
                <div className='two-col'>
                  <div className='control-label'>Model</div>
                  <div className='control'>
                    <SelectInput
                      name='Model'
                      className='input-text w250'
                      value={model}
                      options={modelsOptions}
                      onChange={onChangingModel}
                      addDefaultOption
                      allowZeroOptions
                      error={errors.model}
                    />
                  </div>
                </div>
                <div className='two-col'>
                  <div className='control-label'>Fuel type</div>
                  <div className='control'>
                    <SelectInput
                      name='fuelType'
                      className='input-text w250'
                      value={fuelType}
                      options={fuelTypes}
                      onChange={onChangingFuelType}
                      addDefaultOption
                      error={errors.fuelType}
                    />
                  </div>
                </div>
                <div className='two-col'>
                  <RadioOptions
                    errorMessage={errors.transmission}
                    selectedOption={
                      !transmissionType
                        ? ''
                        : transmissionType === 'M'
                          ? 'Manual'
                          : 'Automatic'
                    }
                    optionsList={transmissionList}
                    setSelectedOption={(value) =>
                      onChangingTransmissionType(value === 'Manual' ? 'M' : 'A')
                    }
                    label={'Transmission'}
                    name='transmission'
                  />
                </div>
                <div className='two-col'>
                  <div className='control-label'>Number of doors</div>
                  <div className='flex-row'>
                    <div className='control flex-column'>
                      <SelectInput
                        name='numberOfDoors'
                        className='input-text w250'
                        value={numberOfDoors}
                        options={numberOfDoorsValues}
                        onChange={onChangingNumberOfDoors}
                        addDefaultOption
                        error={errors.numberOfDoors}
                      />
                    </div>
                    <div className='one-col m1-top'>
                      <ButtonContinue
                        name='find-vehicle-by-search-options'
                        text='Find vehicle'
                        onClick={onFindVehicleBySelectOptions}
                        additionalClassNames='cms-btn-find btn-find'
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
            {showSearchMessage && (
              <div className='one-col error-style'>
                Please click Find Vehicle
              </div>
            )}
            {showManualSearchForm &&
              vehicleSpecificationsOptions.length > 0 && (
                <div className='one-col'>
                  <div className='control-label help-text'>
                    Please select the vehicle specification from the list
                    <ClickableHelpText
                      helpText={InsuranceJourneyHelperText.Pages.VehicleDetails.VehicleSpecification.replace(
                        '[Phone]',
                        site.brand.brandPhoneNumbers.quotesAndRenewalsNumber
                      )}
                    />
                  </div>
                  <div className='control'>
                    <SelectInput
                      name='vehicle-specifications'
                      className='input-text w250'
                      value={vehicleSpecification}
                      options={vehicleSpecificationsOptions}
                      onChange={onChangeVehicleSpecification}
                      addDefaultOption
                      error={errors.vehicleSpecification}
                    />
                  </div>
                </div>
              )}
            {((vrnKnown && vehicle?.modelType && !showManualSearchForm) ||
              vehicleSearchResult === VrnSearchStatus.VrnFound) && (
              <div className='one-col'>
                We have found your vehicle details: <br />
                {vehicle?.modelType}
              </div>
            )}
            {vehicleSearchResult === VrnSearchStatus.ManualNotFound && (
              <div className='one-col error-style'>No matching vehicles</div>
            )}
            {brand.showInsuranceVendorDropdown && (
              <div className='one-col'>
                <div className='control-label'>Please select your vendor</div>
                <div className='control'>
                  <SelectInput
                    name='vendor'
                    className='input-text w250'
                    value={vendor ?? ''}
                    options={vendors}
                    onChange={(value) => onChangeVendor(value)}
                    addDefaultOption
                    error={errors.vendor}
                    readonly={
                      site.user.isDealer ||
                      source === Source.CustomerSelfRegistration
                    }
                  />
                </div>
              </div>
            )}
            {vendor === Vendor.FranchiseRetailer &&
              site.brand.showDealerQuestion === true && (
                <div>
                  <div className='one-col'>
                    <div className='control-label'>Which dealer</div>
                    <div className='control'>
                      <SelectInput
                        name='pickup-dealer'
                        className='input-text w250'
                        value={dealer}
                        options={dealers}
                        onChange={onChangeDealer}
                        addDefaultOption
                        error={errors.pickupDealer}
                        readonly={
                          site.user.isDealer ||
                          source === Source.CustomerSelfRegistration
                        }
                      />
                    </div>
                  </div>
                </div>
              )}

            <div className='one-col'>
              <div className='control-label'>
                What is the purchase date of the vehicle?
              </div>
              <div className='control'>
                <SimpleDateInput
                  name='keys-pick-up-date'
                  value={keysPickupDate}
                  error={errors.keysPickupDate}
                  onDateChange={handleOnChangeKeysPickupDate}
                  minimumDate={minPurchaseDate}
                  maximumDate={maxPurchaseDate}
                />
              </div>
            </div>

            <div className='one-col'>
              <div className='control-label help-text'>
                What's its value? (£)
                <ClickableHelpText
                  helpText={
                    InsuranceJourneyHelperText.Pages.VehicleDetails
                      .WhatsItsValue
                  }
                />
              </div>
              <div className='control'>
                <TextInput
                  name={'carValue'}
                  className='input-text w250'
                  value={carValue}
                  onChange={handleCarValueChange}
                  error={errors.carValue}
                />
              </div>
            </div>

            <div className='one-col'>
              <div className='inline-control'>
                <RadioSelection
                  show={true}
                  error={errors.isItNewOrUsed}
                  selectionName={'Is It?'}
                  fontDisabledClass=''
                  helpText={getDynamicHelpText(
                    InsuranceJourneyHelperText.Pages.VehicleDetails.IsIt,
                    '[Brand]',
                    site.brand.brandName
                  )}>
                  {renderRadioSelectOptions(
                    newOrUsedOptions,
                    'isItNewOrUsed',
                    isItNewOrUsed,
                    onChangeIsItNewOrUsed
                  )}
                </RadioSelection>
              </div>
            </div>

            <div className='one-col'>
              <div className='control-label help-text'>
                When was your car manufactured?
                <ClickableHelpText
                  helpText={
                    InsuranceJourneyHelperText.Pages.VehicleDetails
                      .WhatsYearOfmanufacturer
                  }
                />
              </div>
              <div className='control'>
                <SelectInput
                  name='year-of-manufacture'
                  className='input-text w250'
                  value={yearOfManufacture}
                  options={yearOfManufactureValues}
                  onChange={onChangeYearOfManufacture}
                  addDefaultOption
                  error={errors.yearOfManufacture}
                />
              </div>
            </div>

            <div className='one-col'>
              <div className='inline-control'>
                <RadioSelection
                  show={true}
                  selectionName={
                    'Is it fitted with an anti-theft vehicle tracking device?'
                  }
                  fontDisabledClass=''
                  error={errors.isFittedWithAntiTheftDevice}
                  helpText={
                    InsuranceJourneyHelperText.Pages.VehicleDetails
                      .TrackingDevice
                  }>
                  {renderRadioOptions(
                    DefaultRadioOptions,
                    'isFittedWithAntiTheftDevice',
                    hasTrackingDevice === null
                      ? ''
                      : hasTrackingDevice
                        ? 'Yes'
                        : 'No',
                    (value) => onChangeHasTrackingDevice(value === 'Yes')
                  )}
                </RadioSelection>
              </div>
            </div>

            {hasTrackingDevice && (
              <div className='one-col'>
                <div className='control-label'>Type of tracking device</div>
                <h6>
                  Start typing the name of device and then select from the list.
                  If you can't find your exact match please choose whichever is
                  closest.
                </h6>
                <div className='control'>
                  <SearchTextInput
                    name='typeOfTrackingDevice'
                    className='input-text w250'
                    value={trackingDevice}
                    onChange={handleTypeOfTrackingDeviceChange}
                    onLeave={handleOnLeaveTypeOfTrackingDevice}
                    readOnly={false}
                    listName='trackingDeviceList'
                    listData={trackingDeviceTypes}
                    error={errors.typeOfTrackingDevice}
                  />
                </div>
              </div>
            )}
            <div className='one-col'>
              <div className='inline-control'>
                <RadioSelection
                  show={true}
                  selectionName={'Has it been modified?'}
                  fontDisabledClass=''
                  error={errors.isVehicleModified}
                  helpText={
                    InsuranceJourneyHelperText.Pages.VehicleDetails
                      .HasItBeenModified
                  }>
                  {renderRadioOptions(
                    DefaultRadioOptions,
                    'isVehicleModified',
                    hasBeenModified === null
                      ? ''
                      : hasBeenModified
                        ? 'Yes'
                        : 'No',
                    (value) => onChangeVehicleHasBeenModified(value === 'Yes')
                  )}
                </RadioSelection>
              </div>
            </div>
            {hasBeenModified && (
              <VehicleModifications
                modifications={
                  modifications?.length > 0
                    ? modifications
                    : [
                        {
                          modificationType: '',
                          forDisability: ''
                        } as Modification
                      ]
                }
                errorMessages={errors.modifications}
                modificationTypes={modificationTypes}
                forDisabilityOptions={yesNoSelectOption}
                onUpdateModifications={onUpdateModificationList}
              />
            )}
          </div>
          <div className='even-space-out flex-row form-nav'>
            <ButtonBack
              name='btn-back'
              text='Back'
              onClick={onBackClick}
              additionalClassNames='btn-back'
            />
            <ButtonContinue
              name='btn-save-later'
              text='Save For Later'
              onClick={onSaveLaterClick}
              additionalClassNames='btn-save'
              disabled={savingQuote}
            />
            <ButtonContinue
              name='btn-next'
              text='Next'
              onClick={onNextClick}
              additionalClassNames='btn-continue'
            />
          </div>
        </div>
        <Modal
          role='non-scheme-modal-popup'
          isOpen={showNonSchemePopup}
          className={`insurance-${site.brand.brandName}`}
          toggle={() => setShowNonSchemePopup(false)}>
          <ModalHeader toggle={() => setShowNonSchemePopup(false)}>
            Please Note
          </ModalHeader>
          <ModalBody>
            The vehicle to be insured is NON-SCHEME. Please advise the customer
            to contact Original Insurance on 0800 035 2715
          </ModalBody>
          <ModalFooter>
            <ButtonContinue
              name='modal-btn-continue'
              text={t('customer-product-page.button-quote-expired-continue')}
              onClick={() => setShowNonSchemePopup(false)}
              additionalClassNames='btn-ok'
            />
          </ModalFooter>
        </Modal>
      </>
    </LoadingContainer>
  );
};

export default VehicleDetailsPage;
